import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Days, IServiceSchedule, IServiceScheduleItem, IStatus, IStatusResponse, ScheduleType } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';

export type ServiceDaysSlice = {
    [day in Days]?: string;
};

export interface ServiceScheduleDialogData {
    isCustom: boolean;
    langs: string[];
    translatedValues: any;
    days: string[];
    schedule: IServiceSchedule;
    nonAvailableStatus: IStatusResponse;
    availableStatus: IStatusResponse;
}

const DEFAULT_FROM_TIME = '09:00';
const DEFAULT_TO_TIME = '17:00';

@Component({
    selector: 'app-service-schedule-dialog',
    templateUrl: './service-schedule-dialog.component.html',
    styleUrls: ['./service-schedule-dialog.component.scss'],
})
export class ServiceScheduleDialogComponent implements OnInit {
    daysList: string[] = [];

    startDateMin = new Date();

    endDateMin = new Date();

    is12HourFormat = new Date().toLocaleTimeString([], { hour: '2-digit', hour12: true }).indexOf('0') === 0;

    schedulesDialogForm: FormGroup = this.formBuilder.group({
        schedule: new FormGroup({}),
    });

    activeIntervalIndex: number | null = null;

    get isCustom(): boolean {
        return this.data.isCustom;
    }

    get days(): string[] {
        return this.data.days;
    }

    get name(): FormGroup {
        return this.schedulesDialogForm.get('name') as FormGroup;
    }

    get from(): AbstractControl {
        return this.schedulesDialogForm.get('from');
    }

    get to(): AbstractControl {
        return this.schedulesDialogForm.get('to');
    }

    get schedule(): FormGroup {
        return this.schedulesDialogForm.get('schedule') as FormGroup;
    }

    get datesAreValid(): boolean {
        return this.from.valid && this.to.valid;
    }

    get currentLang(): string {
        return this.translate.currentLang;
    }

    constructor(
        private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: ServiceScheduleDialogData,
        private dialogRef: MatDialogRef<ServiceScheduleDialogComponent>,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        if (!this.data.schedule) return;

        const { type, schedule, name, from, to } = this.data.schedule;

        // Setup form for custom schedules
        if (type === ScheduleType.CUSTOM) {
            // Multi lang name
            this.schedulesDialogForm.addControl('name', new FormGroup({}));
            this.data.langs.forEach((lang) => {
                const value = name ? name[lang] : '';
                this.name.addControl(lang, new FormControl(value, [Validators.required]));
            });

            // Start date and end date
            this.schedulesDialogForm.addControl('from', new FormControl(from || '', [Validators.required]));
            this.schedulesDialogForm.addControl('to', new FormControl(to || '', [Validators.required]));
        }

        if (schedule) {
            this.daysList = Object.keys(schedule);
            this.setupFormWithSchedule(schedule);
        }
    }

    isIntervalCloseable(index: number): boolean {
        if (this.isCustom) {
            if (index > 0) return true;
            return false;
        }
        return true;
    }

    getDayFormArray(day: string): FormArray {
        return this.schedule.get(day) as FormArray;
    }

    fromDateChange(date: Date): void {
        this.schedulesDialogForm.patchValue({ from: date });
        this.formatCustomDays();
        this.endDateMin = date;
        if (this.to.value < date) {
            this.toDateChange(date);
        }
    }

    toDateChange(date: Date): void {
        this.schedulesDialogForm.patchValue({ to: date });
        this.formatCustomDays();
        if (this.from.value > date) {
            this.fromDateChange(date);
        }
    }

    private setupFormWithSchedule(schedule: IServiceScheduleItem): void {
        if (this.isCustom) {
            Object.entries(schedule).forEach(([day, intervals]) => {
                const intervalsForm = this.buildIntervals(intervals);
                this.schedule.addControl(day, new FormArray(intervalsForm));
            });
        } else {
            this.days.forEach((day) => {
                const intervals = schedule[day] ? schedule[day] : [{ from: '', to: '' }];
                const intervalsForm = this.buildIntervals(intervals);
                this.schedule.addControl(day, new FormArray(intervalsForm));
            });
        }
    }

    private buildIntervals(intervals: { from: string; to: string }[]): FormGroup[] {
        const intervalsForm = intervals.map((interval) => {
            const hasInterval = interval.from && interval.to;
            let from = '';
            let to = '';

            if (hasInterval) {
                const [fromHour, fromMinute] = interval.from.split(':');
                const [toHour, toMinute] = interval.to.split(':');

                const paddedFromHour = this.padSingleDigit(parseInt(fromHour, 10));
                const paddedToHour = this.padSingleDigit(parseInt(toHour, 10));

                from = `${paddedFromHour}:${fromMinute}`;
                to = `${paddedToHour}:${toMinute}`;
            } else {
                from = DEFAULT_FROM_TIME;
                to = DEFAULT_TO_TIME;
            }

            return new FormGroup({
                from: new FormControl(from, [Validators.required]),
                to: new FormControl(to, [Validators.required]),
            });
        });

        return intervalsForm;
    }

    private padSingleDigit(hour): string {
        return hour < 10 ? `0${hour}` : `${hour}`;
    }

    // Called when startDate en endDate are changed
    private formatCustomDays(): void {
        if (this.datesAreValid) {
            const fromDate = new Date(this.from.value);
            const toDate = new Date(this.to.value);

            // Reset array for days
            this.daysList = [];

            // Generate days
            for (fromDate; fromDate <= toDate; fromDate.setDate(fromDate.getDate() + 1)) {
                const value = fromDate.toLocaleDateString('en-us', { weekday: 'long' }).toUpperCase();
                this.daysList.push(value);
            }

            // Create fields
            const schedule: IServiceScheduleItem = this.daysList.reduce((acc, day) => {
                // Day already exists in schedule, use it
                if (this.schedule.value[day]) {
                    acc[day] = this.schedule.value[day];
                } else {
                    // Otherwise create a new day
                    acc[day] = [{ from: DEFAULT_FROM_TIME, to: DEFAULT_TO_TIME }];
                }
                return acc;
            }, {});

            // Remove old controls
            Object.keys(this.schedule.controls).forEach((key) => {
                if (!this.daysList.includes(key)) {
                    this.schedule.removeControl(key);
                }
            });

            // Add fields to form
            this.setupFormWithSchedule(schedule);
        }
    }

    setActiveInterval(index: number): void {
        this.activeIntervalIndex = index;
    }

    showAddIntervalButton(day: string): boolean {
        const dayFormArray = this.getDayFormArray(day);
        if (this.activeIntervalIndex === null) {
            return true; // Afficher par défaut si aucun intervalle n'est actif
        }
        const toTimeString = dayFormArray.value[this.activeIntervalIndex].to;
        const formattedTime = this.formatTimeToMinutes(toTimeString);
        return formattedTime < 1439 || formattedTime !== 0;
    }

    private formatTimeToMinutes(timeString: string): number {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours * 60 + minutes;
    }

    handleToTimeChange(index: number): void {
        this.setActiveInterval(index);
    }

    handleAddInterval(day): void {
        const dayFormArray = this.getDayFormArray(day);
        let fromTime = DEFAULT_FROM_TIME;
        let toTime = DEFAULT_TO_TIME;

        if (dayFormArray.value.length) {
            const lastInterval = dayFormArray.value[dayFormArray.value.length - 1];
            fromTime = lastInterval.to;

            const [fromHour] = fromTime.split(':');
            const paddedFromHour = this.padSingleDigit(parseInt(fromHour, 10) + 1);
            const paddedToHour = this.padSingleDigit(parseInt(fromHour, 10) + 2);

            fromTime = `${paddedFromHour}:${fromTime.split(':')[1]}`;
            toTime = `${paddedToHour}:${fromTime.split(':')[1]}`;
        }
        dayFormArray.push(
            new FormGroup({
                from: new FormControl(fromTime, [Validators.required]),
                to: new FormControl(toTime, [Validators.required]),
            }),
        );
    }

    handleRemoveInterval(day, index): void {
        this.setActiveInterval(index);
        this.getDayFormArray(day).removeAt(index);
        if (this.activeIntervalIndex === index) {
            this.activeIntervalIndex = null;
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    saveSchedule(): void {
        if (this.schedulesDialogForm.valid) {
            const payload = {
                ...this.schedulesDialogForm.value,
                type: this.isCustom ? ScheduleType.CUSTOM : ScheduleType.DEFAULT,
            };
            this.dialogRef.close(payload);
        } else {
            this.schedulesDialogForm.markAllAsTouched();
        }
    }

    // setupDefaultSchedule(): void {
    //     const scheduleItem: IServiceScheduleItem = {};

    //     // Define default intervals for each day
    //     this.days.forEach((day) => {
    //         scheduleItem[day] = [{
    //             from: DEFAULT_FROM_TIME,
    //             to: DEFAULT_TO_TIME,
    //         }];
    //     });

    //     // Define schedule
    //     const defaultSchedule: IServiceSchedule = {
    //         _id: uuid.v4(),
    //         type: ScheduleType.DEFAULT,
    //         schedule: scheduleItem,
    //     };

    //     // Build form controls
    //     const defaultScheduleForm = this.formBuilder.group({
    //         _id: new FormControl(defaultSchedule._id),
    //         type: new FormControl(defaultSchedule.type),
    //         schedule: new FormGroup({}),
    //     });

    //     // Controls for each interval in each day
    //     const scheduleItemFormGroup = (defaultScheduleForm.get('schedule') as FormGroup);
    //     Object.entries(defaultSchedule.schedule).forEach(([day, intervals]) => {
    //         const intervalsForm = intervals.map((interval) => new FormGroup({
    //             from: new FormControl(interval.from, [Validators.required]),
    //             to: new FormControl(interval.to, [Validators.required]),
    //         }));
    //         scheduleItemFormGroup.addControl(day, new FormArray(intervalsForm));
    //     });

    //     // Add controls to main form
    //     this.form.setControl('defaultSchedule', defaultScheduleForm);
    // }
}
